// AttachmentSectionEnhanced.jsx - Redesigned attachment section with consistent UI
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  Chip,
  Fade,
  LinearProgress
} from '@mui/material';
import {
  AttachFile as AttachmentIcon,
  Description as DocumentIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { api } from '../../services/api';
import { SecureFileDownload, ImagePreviewModal } from '../ui';
import ImageViewer from '../ui/ImageViewer';
import useTaskStore from '../../stores/taskStore';

/**
 * Enhanced AttachmentSection with consistent UI styling
 * Matches the design patterns of checklist, subtasks, and description sections
 */
const AttachmentSectionEnhanced = ({ 
  taskId, 
  attachments = [], 
  canEdit = false,
  onAttachmentUpdate
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [showAlert, setShowAlert] = useState(false);
  const [localAttachments, setLocalAttachments] = useState(attachments);
  const [previewImage, setPreviewImage] = useState(null);
  const [hoveredAttachment, setHoveredAttachment] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  
  const { fetchTask, currentTask } = useTaskStore();
  
  // Update local attachments when props change
  useEffect(() => {
    setLocalAttachments(attachments);
  }, [attachments]);

  const effectiveTaskId = taskId || currentTask?._id;
  
  // Check if file is an image
  const isImage = (fileType) => {
    return fileType && fileType.startsWith('image/');
  };
  
  // Get file icon with consistent styling
  const getFileIcon = (fileType, size = 18) => {
    const iconProps = { sx: { fontSize: size } };
    
    if (!fileType) return <FileIcon {...iconProps} sx={{ ...iconProps.sx, color: 'text.secondary' }} />;
    
    if (fileType.includes('image/')) {
      return <ImageIcon {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.primary.main }} />;
    } else if (fileType.includes('pdf')) {
      return <PdfIcon {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.error.main }} />;
    } else if (fileType.includes('document') || fileType.includes('sheet') || fileType.includes('presentation')) {
      return <DocumentIcon {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.info.main }} />;
    } else {
      return <FileIcon {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.warning.main }} />;
    }
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
  };
  
  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    try {
      return format(new Date(date), 'MMM d');
    } catch (error) {
      return '';
    }
  };
  
  // Show alert message
  const showMessage = (message, severity = 'info') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setShowAlert(true);
  };
  
  // Handle drag and drop events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (canEdit && e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (!canEdit || !effectiveTaskId) {
      if (!effectiveTaskId) {
        showMessage('Task ID is missing. Cannot upload files.', 'error');
      }
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await uploadFiles(files);
    }
  };

  // Common file upload logic
  const uploadFiles = async (files) => {
    if (files.length === 0 || !effectiveTaskId) {
      if (!effectiveTaskId) {
        showMessage('Task ID is missing. Cannot upload files.', 'error');
      }
      return;
    }
    
    setIsUploading(true);
    
    // Create placeholder attachment objects
    const uploadingFilesList = files.map((file, index) => ({
      _id: `uploading-${Date.now()}-${index}`,
      name: file.name,
      file: {
        originalname: file.name,
        size: file.size,
        mimetype: file.type || 'application/octet-stream'
      },
      uploadProgress: 0,
      isUploading: true,
      uploadError: null
    }));
    
    setLocalAttachments(prev => [...prev, ...uploadingFilesList]);
    setUploadingFiles(uploadingFilesList);
    
    try {
      const newAttachments = [];
      let successCount = 0;
      let failCount = 0;
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const tempId = uploadingFilesList[i]._id;
          setLocalAttachments(prev => prev.map(att => 
            att._id === tempId 
              ? { ...att, uploadProgress: 50 }
              : att
          ));
        
          const response = await api.uploadFile(
            `/tasks/${effectiveTaskId}/attachments`,
            file,
            'file',
            { name: file.name }
          );
          
          if (response.data && response.data.attachment) {
            newAttachments.push(response.data.attachment);
            successCount++;
            
            setLocalAttachments(prev => prev.map(att => 
              att._id === tempId 
                ? response.data.attachment
                : att
            ));
          }
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          failCount++;
          
          const tempId = uploadingFilesList[i]._id;
          setLocalAttachments(prev => prev.map(att => 
            att._id === tempId 
              ? { ...att, isUploading: false, uploadError: error.message || 'Upload failed' }
              : att
          ));
        }
      }
      
      if (onAttachmentUpdate && newAttachments.length > 0) {
        onAttachmentUpdate(localAttachments.filter(att => !att.isUploading && !att.uploadError));
      }
      
      if (successCount > 0 && failCount === 0) {
        showMessage(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`, 'success');
      } else if (successCount > 0 && failCount > 0) {
        showMessage(`${successCount} uploaded, ${failCount} failed`, 'warning');
      } else if (failCount > 0) {
        showMessage(`Failed to upload ${failCount} file${failCount > 1 ? 's' : ''}`, 'error');
      }
      
      if (successCount > 0) {
        await fetchTask(effectiveTaskId);
      }
    } finally {
      setIsUploading(false);
      
      setTimeout(() => {
        setLocalAttachments(prev => prev.filter(att => !att.uploadError));
        setUploadingFiles([]);
      }, 5000);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle file input upload
  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    await uploadFiles(files);
  };
  
  // Handle download error
  const handleDownloadError = (error) => {
    showMessage(`Download failed: ${error.message || 'Unknown error'}`, 'error');
  };
  
  // Handle image preview
  const handleImageClick = (file) => {
    setPreviewImage(file);
  };
  
  // Handle file delete
  const handleDelete = async (attachmentId) => {
    if (!effectiveTaskId || !attachmentId) return;

    try {
      setLocalAttachments(prevAttachments =>
        prevAttachments.filter(att => att._id !== attachmentId)
      );

      await api.delete(`/tasks/${effectiveTaskId}/attachments/${attachmentId}`);

      if (onAttachmentUpdate) {
        onAttachmentUpdate(localAttachments.filter(att => att._id !== attachmentId));
      }

      await fetchTask(effectiveTaskId);
      showMessage('Attachment deleted', 'success');
    } catch (error) {
      showMessage(`Delete failed: ${error.message || 'Unknown error'}`, 'error');
      setLocalAttachments(attachments);
    }
  };

  // Handle drag start for reordering
  const handleReorderDragStart = () => {
    // Visual feedback that drag started
  };

  // Handle drag end and reorder attachments
  const handleReorderDragEnd = async (result) => {
    // No destination or same position - do nothing
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // Reorder array optimistically
    const reordered = Array.from(localAttachments);
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destinationIndex, 0, removed);

    setLocalAttachments(reordered);

    // Send to backend
    try {
      const orderedIds = reordered.map(att => att._id);
      await api.patch(`/tasks/${effectiveTaskId}`, {
        attachmentOrder: orderedIds
      });

      showMessage('Attachments reordered', 'success');

      // Optionally refresh from server
      if (onAttachmentUpdate) {
        onAttachmentUpdate(reordered);
      }
    } catch (error) {
      console.error('Error reordering attachments:', error);
      showMessage('Failed to reorder attachments', 'error');
      // Revert on error
      setLocalAttachments(attachments);
    }
  };

  return (
    <Box>
      {/* Container with consistent styling + drag and drop */}
      <Box 
        sx={{
          p: 1.5,
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          transition: 'all 0.2s ease',
          position: 'relative',
          ...(canEdit && {
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.3),
            }
          }),
          ...(isDragging && {
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 1,
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              pointerEvents: 'none',
            }
          })
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: localAttachments.length > 0 ? 1.5 : 0,
          pb: localAttachments.length > 0 ? 1 : 0,
          borderBottom: localAttachments.length > 0 ? `1px solid ${alpha(theme.palette.divider, 0.06)}` : 'none'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachmentIcon sx={{ 
              fontSize: 16, 
              color: theme.palette.text.secondary 
            }} />
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem'
              }}
            >
              Attachments
            </Typography>
            {localAttachments.length > 0 && (
              <Chip
                label={localAttachments.length}
                size="small"
                sx={{
                  height: 18,
                  minWidth: 18,
                  fontSize: '0.7rem',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  '& .MuiChip-label': {
                    px: 0.75
                  }
                }}
              />
            )}
          </Box>
          
          {/* Upload button in header */}
          {canEdit && (
            <>
              <input
                ref={fileInputRef}
                accept="*/*"
                style={{ display: 'none' }}
                id="attachment-upload-enhanced"
                type="file"
                multiple
                onChange={handleUpload}
                disabled={isUploading}
              />
              <label htmlFor="attachment-upload-enhanced">
                <IconButton
                  component="span"
                  size="small"
                  disabled={isUploading}
                  sx={{
                    p: 0.5,
                    color: theme.palette.text.secondary,
                    opacity: isUploading ? 0.5 : 1,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                    }
                  }}
                >
                  {isUploading ? <CircularProgress size={16} /> : <AddIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </label>
            </>
          )}
        </Box>
        
        {/* Attachments List */}
        {localAttachments.length > 0 && (
          <DragDropContext
            onDragStart={handleReorderDragStart}
            onDragEnd={handleReorderDragEnd}
          >
            <Droppable droppableId="attachments-list">
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    mb: canEdit ? 1 : 0,
                    backgroundColor: snapshot.isDraggingOver
                      ? alpha(theme.palette.primary.main, 0.05)
                      : 'transparent',
                    transition: 'background-color 0.2s',
                    borderRadius: 0.5
                  }}
                >
                  {localAttachments.map((attachment, index) => {
              const file = attachment.file || attachment;
              const fileName = attachment.name || file.originalname || file.name || 'Unnamed file';
              const fileType = file.mimetype || file.type;
              const fileSize = file.size;
              const uploadDate = attachment.uploadedAt || attachment.createdAt;

              // Don't make uploading or error attachments draggable
              const isDraggable = !attachment.isUploading && !attachment.uploadError && canEdit;

              if (!isDraggable) {
                // Render non-draggable attachment (uploading or error)
                return (
                  <Fade in key={attachment._id || file.key} timeout={200 * (index + 1)}>
                    <Box
                      onMouseEnter={() => setHoveredAttachment(attachment._id)}
                      onMouseLeave={() => setHoveredAttachment(null)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        py: 0.75,
                        bgcolor: alpha(theme.palette.action.hover, 0.02),
                        borderRadius: 0.5,
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        transition: 'all 0.15s ease',
                        cursor: 'default',
                        opacity: attachment.uploadError ? 0.6 : 1,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.08),
                        borderColor: alpha(theme.palette.divider, 0.15),
                      }
                    }}
                    onClick={() => isImage(fileType) && !attachment.isUploading && !attachment.uploadError && handleImageClick(file)}
                  >
                    {/* File Icon/Thumbnail */}
                    <Box sx={{ 
                      width: 32,
                      height: 32,
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {isImage(fileType) ? (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 0.5,
                            overflow: 'hidden',
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            bgcolor: 'grey.100'
                          }}
                        >
                          <ImageViewer
                            file={file}
                            displayType="thumbnail"
                            alt={fileName}
                            sx={{ width: 32, height: 32 }}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            fallbackToOriginal={true}
                          />
                        </Box>
                      ) : (
                        getFileIcon(fileType)
                      )}
                    </Box>
                    
                    {/* File Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem',
                          mb: 0.25
                        }}
                      >
                        {fileName}
                      </Typography>
                      
                      {/* Meta Info */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.75
                      }}>
                        {attachment.isUploading ? (
                          <>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              Uploading...
                            </Typography>
                            <Box sx={{ flex: 1, maxWidth: 100 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={attachment.uploadProgress || 0}
                                sx={{
                                  height: 2,
                                  borderRadius: 1,
                                  bgcolor: alpha(theme.palette.divider, 0.2),
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 1,
                                    bgcolor: theme.palette.primary.main,
                                  }
                                }}
                              />
                            </Box>
                          </>
                        ) : attachment.uploadError ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ErrorIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
                            <Typography variant="caption" sx={{ color: theme.palette.error.main, fontSize: '0.75rem' }}>
                              {attachment.uploadError}
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {formatFileSize(fileSize)}
                            </Typography>
                            {uploadDate && (
                              <>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                  •
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                  {formatDate(uploadDate)}
                                </Typography>
                              </>
                            )}
                            {isImage(fileType) && (
                              <>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                  •
                                </Typography>
                                <Typography variant="caption" sx={{ color: theme.palette.info.main, fontSize: '0.75rem' }}>
                                  Click to preview
                                </Typography>
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Actions */}
                    {!attachment.isUploading && (
                      <Fade in={hoveredAttachment === attachment._id}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {!attachment.uploadError && (
                            <SecureFileDownload
                              file={file}
                              tooltip="Download"
                              iconButtonProps={{
                                size: "small",
                                sx: { 
                                  p: 0.5,
                                  color: theme.palette.primary.main,
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                                  }
                                }
                              }}
                            />
                          )}
                          {canEdit && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (attachment.uploadError) {
                                  setLocalAttachments(prev => prev.filter(att => att._id !== attachment._id));
                                } else {
                                  handleDelete(attachment._id);
                                }
                              }}
                              sx={{
                                p: 0.5,
                                color: theme.palette.error.main,
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.15),
                                }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Fade>
                    )}
                  </Box>
                </Fade>
              );
            }

            // Render draggable attachment
            return (
              <Draggable key={attachment._id} draggableId={attachment._id} index={index}>
                {(dragProvided, dragSnapshot) => (
                  <Fade in timeout={200 * (index + 1)}>
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      onMouseEnter={() => setHoveredAttachment(attachment._id)}
                      onMouseLeave={() => setHoveredAttachment(null)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        py: 0.75,
                        bgcolor: dragSnapshot.isDragging
                          ? alpha(theme.palette.primary.main, 0.08)
                          : alpha(theme.palette.action.hover, 0.02),
                        borderRadius: 0.5,
                        border: `1px solid ${dragSnapshot.isDragging
                          ? theme.palette.primary.main
                          : alpha(theme.palette.divider, 0.08)}`,
                        transition: 'all 0.15s ease',
                        cursor: canEdit ? 'grab' : 'default',
                        boxShadow: dragSnapshot.isDragging
                          ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          : 'none',
                        '&:hover': {
                          bgcolor: dragSnapshot.isDragging
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.action.hover, 0.08),
                          borderColor: dragSnapshot.isDragging
                            ? theme.palette.primary.main
                            : alpha(theme.palette.divider, 0.15),
                        },
                        '&:active': {
                          cursor: canEdit ? 'grabbing' : 'default',
                        }
                      }}
                      onClick={() => isImage(fileType) && handleImageClick(file)}
                    >
                      {/* File Icon/Thumbnail */}
                      <Box sx={{
                        width: 32,
                        height: 32,
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {isImage(fileType) ? (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 0.5,
                              overflow: 'hidden',
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                              bgcolor: 'grey.100'
                            }}
                          >
                            <ImageViewer
                              file={file}
                              displayType="thumbnail"
                              alt={fileName}
                              sx={{ width: 32, height: 32 }}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              fallbackToOriginal={true}
                            />
                          </Box>
                        ) : (
                          getFileIcon(fileType)
                        )}
                      </Box>

                      {/* File Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.875rem',
                            mb: 0.25
                          }}
                        >
                          {fileName}
                        </Typography>

                        {/* Meta Info */}
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75
                        }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {formatFileSize(fileSize)}
                          </Typography>
                          {uploadDate && (
                            <>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                •
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {formatDate(uploadDate)}
                              </Typography>
                            </>
                          )}
                          {isImage(fileType) && (
                            <>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                •
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.info.main, fontSize: '0.75rem' }}>
                                Click to preview
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>

                      {/* Actions */}
                      <Fade in={hoveredAttachment === attachment._id}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <SecureFileDownload
                            file={file}
                            tooltip="Download"
                            iconButtonProps={{
                              size: "small",
                              sx: {
                                p: 0.5,
                                color: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                                }
                              }
                            }}
                          />
                          {canEdit && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(attachment._id);
                              }}
                              sx={{
                                p: 0.5,
                                color: theme.palette.error.main,
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.15),
                                }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Fade>
                    </Box>
                  </Fade>
                )}
              </Draggable>
            );
          })}
          {provided.placeholder}
        </Box>
      )}
    </Droppable>
  </DragDropContext>
)}

        {/* Empty state message */}
        {localAttachments.length === 0 && !canEdit && (
          <Box sx={{ 
            textAlign: 'center',
            py: 2,
            color: 'text.secondary'
          }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
              No attachments
            </Typography>
          </Box>
        )}
        
        {/* Drag overlay */}
        {isDragging && canEdit && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 1,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.95),
                borderRadius: 1,
                border: `2px dashed ${theme.palette.primary.main}`,
              }}
            >
              <UploadIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  textAlign: 'center',
                }}
              >
                Drop files to upload
              </Typography>
            </Box>
          </Box>
        )}

        {/* Add Button - Always visible when canEdit */}
        {canEdit && (
          <label htmlFor="attachment-upload-enhanced" style={{ cursor: 'pointer' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 0.75,
                mt: localAttachments.length > 0 ? 0.5 : 0,
                borderRadius: 0.5,
                cursor: 'pointer',
                border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
                color: theme.palette.text.secondary,
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  color: theme.palette.primary.main,
                },
                ...(isUploading && {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  pointerEvents: 'none'
                })
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {isUploading ? 'Uploading...' : 'Add attachment or drag & drop files'}
              </Typography>
            </Box>
          </label>
        )}
      </Box>
      
      {/* Alert message */}
      <Snackbar 
        open={showAlert} 
        autoHideDuration={3000} 
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowAlert(false)} 
          severity={alertSeverity} 
          sx={{ width: '100%' }}
          variant="filled"
          elevation={6}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
      
      {/* Image Preview Modal */}
      <ImagePreviewModal
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        file={previewImage}
      />
    </Box>
  );
};

export default AttachmentSectionEnhanced;